import React, { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { MagnifyingGlassIcon, PlusIcon, BookOpenIcon, DocumentTextIcon } from '@heroicons/react/24/outline'

interface KnowledgeArticle {
  id: string
  title: string
  content: string
  excerpt?: string
  scope: 'internal' | 'client_specific' | 'global'
  status: 'draft' | 'published' | 'archived'
  viewCount: number
  helpfulCount: number
  notHelpfulCount: number
  createdAt: string
  updatedAt: string
  category?: {
    id: string
    name: string
    description?: string
  }
  author: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
  clientOrg?: {
    id: string
    name: string
    domain: string
  }
}

interface KnowledgeCategory {
  id: string
  name: string
  description?: string
  scope: 'internal' | 'client_specific' | 'global'
  parentId?: string
  isActive: boolean
  sortOrder: number
  _count: {
    articles: number
  }
}

const KnowledgeBase: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [articles, setArticles] = useState<KnowledgeArticle[]>([])
  const [categories, setCategories] = useState<KnowledgeCategory[]>([])
  const [filteredArticles, setFilteredArticles] = useState<KnowledgeArticle[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedScope, setSelectedScope] = useState<string>('')
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [createForm, setCreateForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    categoryId: '',
    scope: 'global' as 'internal' | 'client_specific' | 'global',
    status: 'draft' as 'draft' | 'published'
  })

  const isStaff = user?.roles?.some(role => ['administrator', 'support_staff', 'manager'].includes(role))

  useEffect(() => {
    loadKnowledgeBase()
  }, [])

  useEffect(() => {
    filterArticles()
  }, [articles, selectedCategory, searchQuery, selectedScope])

  const loadKnowledgeBase = async () => {
    try {
      setIsLoading(true)
      
      const [articlesResponse, categoriesResponse] = await Promise.all([
        axios.get('/api/knowledge'),
        axios.get('/api/knowledge/categories')
      ])

      if (articlesResponse.data.success && categoriesResponse.data.success) {
        setArticles(articlesResponse.data.data)
        setCategories(categoriesResponse.data.data)
      } else {
        toast.error('Failed to load knowledge base')
      }
    } catch (error) {
      console.error('Error loading knowledge base:', error)
      toast.error('Failed to load knowledge base')
    } finally {
      setIsLoading(false)
    }
  }

  const filterArticles = () => {
    let filtered = [...articles]

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter(article => article.category?.id === selectedCategory)
    }

    // Filter by scope
    if (selectedScope) {
      filtered = filtered.filter(article => article.scope === selectedScope)
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(query) ||
        article.content.toLowerCase().includes(query) ||
        (article.excerpt && article.excerpt.toLowerCase().includes(query))
      )
    }

    setFilteredArticles(filtered)
  }

  const getScopeLabel = (scope: string) => {
    switch (scope) {
      case 'internal':
        return 'Internal'
      case 'client_specific':
        return 'Client-Specific'
      case 'global':
        return 'Global'
      default:
        return scope
    }
  }

  const getScopeColor = (scope: string) => {
    switch (scope) {
      case 'internal':
        return 'bg-purple-100 text-purple-800'
      case 'client_specific':
        return 'bg-blue-100 text-blue-800'
      case 'global':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800'
      case 'draft':
        return 'bg-yellow-100 text-yellow-800'
      case 'archived':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const handleCreateArticle = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!createForm.title.trim() || !createForm.content.trim()) {
      toast.error('Title and content are required')
      return
    }

    try {
      setIsCreating(true)
      
      const response = await axios.post('/api/knowledge', createForm)
      
      if (response.data.success) {
        toast.success('Article created successfully!')
        setShowCreateForm(false)
        setCreateForm({
          title: '',
          content: '',
          excerpt: '',
          categoryId: '',
          scope: 'global',
          status: 'draft'
        })
        // Reload articles to show the new one
        loadKnowledgeBase()
      } else {
        toast.error(response.data.error || 'Failed to create article')
      }
    } catch (error: any) {
      console.error('Error creating article:', error)
      toast.error(error.response?.data?.error || 'Failed to create article')
    } finally {
      setIsCreating(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading knowledge base...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-semibold text-gray-900">Knowledge Base</h1>
          <p className="mt-2 text-sm text-gray-700">
            Find answers to common questions and helpful resources
          </p>
        </div>
        {isStaff && (
          <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              onClick={() => setShowCreateForm(true)}
              className="btn-primary inline-flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              New Article
            </button>
          </div>
        )}
      </div>

      {/* Filters and Search */}
      <div className="mt-8 space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search knowledge base..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field pl-10 w-full"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          {/* Category Filter */}
          <div>
            <label htmlFor="category-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              id="category-filter"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="input-field"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name} ({category._count.articles})
                </option>
              ))}
            </select>
          </div>

          {/* Scope Filter */}
          <div>
            <label htmlFor="scope-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Scope
            </label>
            <select
              id="scope-filter"
              value={selectedScope}
              onChange={(e) => setSelectedScope(e.target.value)}
              className="input-field"
            >
              <option value="">All Scopes</option>
              <option value="global">Global</option>
              <option value="client_specific">Client-Specific</option>
              {isStaff && <option value="internal">Internal</option>}
            </select>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="mt-6">
        <p className="text-sm text-gray-600">
          Showing {filteredArticles.length} of {articles.length} articles
        </p>
      </div>

      {/* Articles Grid */}
      <div className="mt-8">
        {filteredArticles.length === 0 ? (
          <div className="text-center py-12">
            <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No articles found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery || selectedCategory || selectedScope 
                ? 'Try adjusting your search or filters'
                : 'Get started by creating the first article'
              }
            </p>
          </div>
        ) : (
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredArticles.map((article) => (
                <article
                  key={article.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                  onClick={() => navigate(`/knowledge/${article.id}`)}
                >
                <div className="p-6">
                  {/* Article Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {article.title}
                      </h3>
                    </div>
                  </div>

                  {/* Article Meta */}
                  <div className="flex items-center space-x-2 mb-3">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getScopeColor(article.scope)}`}>
                      {getScopeLabel(article.scope)}
                    </span>
                    {isStaff && (
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(article.status)}`}>
                        {article.status.charAt(0).toUpperCase() + article.status.slice(1)}
                      </span>
                    )}
                  </div>

                  {/* Excerpt */}
                  {article.excerpt && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {article.excerpt}
                    </p>
                  )}

                  {/* Category */}
                  {article.category && (
                    <div className="mb-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <DocumentTextIcon className="h-3 w-3 mr-1" />
                        {article.category.name}
                      </span>
                    </div>
                  )}

                  {/* Article Footer */}
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <span>👁️ {article.viewCount}</span>
                      <span>👍 {article.helpfulCount}</span>
                      <span>👎 {article.notHelpfulCount}</span>
                    </div>
                    <span>{formatDate(article.updatedAt)}</span>
                  </div>

                  {/* Author */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      By {article.author.firstName} {article.author.lastName}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* Article Creation Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create New Article</h3>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleCreateArticle} className="space-y-4">
                {/* Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    required
                    className="input-field w-full"
                    value={createForm.title}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Article title"
                  />
                </div>

                {/* Content */}
                <div>
                  <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                    Content *
                  </label>
                  <textarea
                    id="content"
                    required
                    rows={6}
                    className="input-field w-full"
                    value={createForm.content}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Article content (supports markdown)"
                  />
                </div>

                {/* Excerpt */}
                <div>
                  <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-1">
                    Excerpt
                  </label>
                  <textarea
                    id="excerpt"
                    rows={3}
                    className="input-field w-full"
                    value={createForm.excerpt}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, excerpt: e.target.value }))}
                    placeholder="Brief summary of the article (optional)"
                  />
                </div>

                {/* Category */}
                <div>
                  <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    id="categoryId"
                    className="input-field w-full"
                    value={createForm.categoryId}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, categoryId: e.target.value }))}
                  >
                    <option value="">No Category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Scope and Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="scope" className="block text-sm font-medium text-gray-700 mb-1">
                      Scope
                    </label>
                    <select
                      id="scope"
                      className="input-field w-full"
                      value={createForm.scope}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, scope: e.target.value as any }))}
                    >
                      <option value="global">Global</option>
                      <option value="internal">Internal (Staff Only)</option>
                      <option value="client_specific">Client-Specific</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      id="status"
                      className="input-field w-full"
                      value={createForm.status}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, status: e.target.value as any }))}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="btn-secondary"
                    disabled={isCreating}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={isCreating}
                  >
                    {isCreating ? 'Creating...' : 'Create Article'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default KnowledgeBase
